import { getHeaderCells } from "./headerCells";
import type {
  ParticipantListCombinationPerson,
  ParticipantListCombinationResponse,
  ParticipantListCombinationRoom,
  ParticipantListRequiredFields,
  ResourceResponse,
} from "./participantListApi";
import type { Cell } from "@silevis/reactgrid";
import { NonEditableCell } from "@silevis/reactgrid";
import range from "lodash/range";
import { emptyParticipantCell, parseIsoDate } from "./getRoomsCells";
import { styleIfDifferent } from "./styleIfDifferent";

import { DATE_FORMAT } from "../../dayjs";

const cabinDividerStyle = { borderTop: "2px solid #f0f0f4" };

export function getReadonlyRoomsCells(
  combination: ParticipantListCombinationResponse,
  previousCombination: ParticipantListCombinationResponse | undefined,
  requiredFields: ParticipantListRequiredFields,
  showDiffs: boolean,
) {
  return [
    ...getHeaderCells(requiredFields),
    ...getRoomsCells(combination, previousCombination, requiredFields, showDiffs),
  ];
}

function getRoomsCells(
  combination: ParticipantListCombinationResponse,
  previousCombination: ParticipantListCombinationResponse | undefined,
  requiredFields: ParticipantListRequiredFields,
  showDiffs: boolean,
): Cell[] {
  const maxColumnsCount = 6 + Object.values(requiredFields).filter((v) => v == true).length;
  return Object.keys(combination)
    .flatMap((t) =>
      roomRowsForRoomType(
        combination[t].rooms,
        previousCombination ? previousCombination[t]?.rooms : undefined,
        combination[t].resources,
        Number.parseInt(t),
        requiredFields,
        showDiffs,
      ),
    )
    .flatMap((peopleRows, idx) => peopleRows.map((r) => ({ row: r, roomNumber: idx + 1 })))
    .flatMap(({ row, roomNumber }, idx) => {
      if (row.length === maxColumnsCount) {
        const cabinBorderStyle = roomNumber > 1 ? cabinDividerStyle : undefined;
        const withBorder = (cell: Omit<Cell, "rowIndex">): Omit<Cell, "rowIndex"> =>
          cabinBorderStyle
            ? { ...cell, props: { ...cell.props, style: { ...cell.props?.style, ...cabinBorderStyle } } }
            : cell;
        const [roomNumberCell, roomTypeCell, ordinalCell, ...personCellsRow] = row;
        return [
          {
            ...roomNumberCell,
            rowIndex: idx + 1,
            props: { value: roomNumber, style: cabinBorderStyle },
          },
          { ...withBorder(roomTypeCell), rowIndex: idx + 1 },
          {
            ...ordinalCell,
            rowIndex: idx + 1,
            props: { value: idx + 1, style: cabinBorderStyle },
          },
          ...personCellsRow.map((c) => ({ ...withBorder(c), rowIndex: idx + 1 })),
        ];
      }
      return [
        { rowIndex: idx + 1, colIndex: 2, Template: NonEditableCell, props: { value: idx + 1 } },
        ...row.map((c) => ({ ...c, rowIndex: idx + 1 })),
      ];
    });
}

function roomRowsForRoomType(
  rooms: ParticipantListCombinationRoom[],
  previousRooms: ParticipantListCombinationRoom[] | undefined,
  resources: ResourceResponse[],
  numberOfPeople: number,
  requiredFields: ParticipantListRequiredFields,
  showDiffs: boolean,
): Omit<Cell, "rowIndex">[][][] {
  return rooms.map((r, idx) =>
    peopleRowsForRoom(
      r,
      previousRooms ? previousRooms[idx] : undefined,
      resources,
      numberOfPeople,
      requiredFields,
      showDiffs,
    ),
  );
}

function peopleRowsForRoom(
  room: ParticipantListCombinationRoom,
  previousRoom: ParticipantListCombinationRoom | undefined,
  resources: ResourceResponse[],
  numberOfPeople: number,
  requiredFields: ParticipantListRequiredFields,
  showDiffs: boolean,
): Omit<Cell, "rowIndex">[][] {
  const people = room.people;
  const previousPeople = previousRoom?.people;
  const firstRow: Omit<Cell, "rowIndex">[] = [
    {
      colIndex: 0,
      rowSpan: numberOfPeople,
      Template: NonEditableCell,
    },
    {
      colIndex: 1,
      Template: NonEditableCell,
      rowSpan: numberOfPeople,
      props: { value: resources.map((r) => r.option.name).join("\n") },
    },
    {
      colIndex: 2,
      Template: NonEditableCell,
    },
    ...personCells(people[0], previousPeople ? previousPeople[0] : undefined, requiredFields, showDiffs),
  ];
  const otherRows = range(1, numberOfPeople).map((personIdx) =>
    personCells(people[personIdx], previousPeople ? previousPeople[personIdx] : undefined, requiredFields, showDiffs),
  );
  return [firstRow, ...otherRows];
}

function personCells(
  person: ParticipantListCombinationPerson | null,
  previousPerson: ParticipantListCombinationPerson | null | undefined,
  requiredFields: ParticipantListRequiredFields,
  showDiffs: boolean,
) {
  const genderToDisplayValue = {
    FEMALE: "F",
    MALE: "M",
  };
  const typeToDisplayValue = {
    PARTICIPANT: "Tourist",
    DRIVER: "Driver",
    TOUR_LEADER: "Tour leader",
  };
  return [
    cellForPerson(
      (p) => (p ? typeToDisplayValue[p.type] : "Unused bed"),
      previousPerson ? typeToDisplayValue[previousPerson.type as keyof typeof typeToDisplayValue] : "Unused bed",
      showDiffs,
    ),
    cellForPerson((p) => p.name ?? "", previousPerson?.name ?? "", showDiffs),
    cellForPerson((p) => p.surname ?? "", previousPerson?.surname ?? "", showDiffs),
    cellForPerson(
      (p) => parseIsoDate(p.dateOfBirth)?.format(DATE_FORMAT),
      parseIsoDate(previousPerson?.dateOfBirth)?.format(DATE_FORMAT),
      showDiffs,
      requiredFields.dateOfBirth,
    ),
    cellForPerson(
      (p) => (p.gender ? genderToDisplayValue[p.gender as keyof typeof genderToDisplayValue] : undefined),
      previousPerson?.gender
        ? genderToDisplayValue[previousPerson.gender as keyof typeof genderToDisplayValue]
        : undefined,
      showDiffs,
      requiredFields.gender,
    ),
    cellForPerson((p) => p.citizenship ?? "", previousPerson?.citizenship ?? "", showDiffs, requiredFields.citizenship),
    cellForPerson(
      (p) => p.documentNumber ?? "",
      previousPerson?.documentNumber ?? "",
      showDiffs,
      requiredFields.documentNumber,
    ),
    cellForPerson(
      (p) => parseIsoDate(p.documentExpiryDate)?.format(DATE_FORMAT),
      parseIsoDate(previousPerson?.documentExpiryDate)?.format(DATE_FORMAT),
      showDiffs,
      requiredFields.documentExpiryDate,
    ),
  ]
    .filter((v): v is NonNullable<typeof v> => v !== null)
    .map((f) => f(person))
    .map((c, idx) => ({ ...c, colIndex: idx + 3 }));
}

function cellForPerson(
  f: (t: ParticipantListCombinationPerson) => string | number | undefined,
  previousValue: string | number | undefined,
  showDiffs: boolean,
  isRequired = true,
): ((t: ParticipantListCombinationPerson | null) => Omit<Cell, "rowIndex" | "colIndex">) | null {
  if (!isRequired) {
    return null;
  }
  return (person: ParticipantListCombinationPerson | null) => {
    if (person) {
      const value = f(person);
      return {
        Template: NonEditableCell,
        props: {
          value,
          style: styleIfDifferent(value, previousValue, showDiffs),
        },
      };
    }
    return emptyParticipantCell;
  };
}
